import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Sparkles, Moon, Sun } from 'lucide-react';

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const { toggleTheme, isGothic } = useTheme();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(formData.name, formData.email, formData.password);
      toast.success('Account created successfully!');
      
      // Check if there's a pending prompt from the examples page
      const pendingPrompt = sessionStorage.getItem('pendingPrompt');
      const returnTo = location.state?.returnTo;
      
      if (pendingPrompt && returnTo) {
        sessionStorage.removeItem('pendingPrompt');
        navigate(returnTo, { state: { prefillPrompt: pendingPrompt } });
      } else {
        navigate('/generator');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0507] flex" data-testid="register-page">
      {/* Theme Toggle - Fixed position */}
      <button
        onClick={toggleTheme}
        className="fixed top-4 right-4 z-50 p-2 rounded-lg bg-[var(--bg-surface)] border border-[var(--border-gold)] hover:bg-[var(--bg-velvet)] transition-all"
        data-testid="theme-toggle"
        aria-label={isGothic ? 'Switch to Manuscript mode' : 'Switch to Gothic mode'}
      >
        {isGothic ? (
          <Sun className="w-5 h-5 text-[var(--gold)]" />
        ) : (
          <Moon className="w-5 h-5 text-[var(--gold)]" />
        )}
      </button>

      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden stained-glass-bg">
        <div className="absolute inset-0 bg-gradient-to-br from-[#722F37]/80 to-[#0D0507]/90" />
        
        {/* Gothic decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          <div className="absolute top-20 left-16 w-32 h-[1px] bg-gradient-to-r from-[#D4AF37]/40 to-transparent" />
          <div className="absolute bottom-32 right-20 w-24 h-[1px] bg-gradient-to-l from-[#D4AF37]/30 to-transparent" />
          <div className="absolute top-1/3 right-12 w-2 h-2 rounded-full bg-[#D4AF37]/30" />
          <div className="absolute bottom-1/4 left-20 w-3 h-3 rounded-full bg-[#D4AF37]/20" />
        </div>
        
        <div className="relative z-10 p-16 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-2xl font-cinzel font-bold text-[#F4EFE6]">
              Funtz<span className="text-[#D4AF37]">Guntz</span>AI
            </span>
          </Link>
          
          <div>
            <div className="w-16 h-16 gothic-border rounded-lg flex items-center justify-center mb-8">
              <Sparkles className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h1 className="text-4xl font-gothic text-[#F4EFE6] mb-6 leading-tight">
              Start creating
            </h1>
            <p className="text-xl text-[#C2B49A] max-w-md font-serif">
              Join thousands of designers crafting unique fonts with AI
            </p>
          </div>
          
          <p className="text-sm text-[#C2B49A]/60 font-serif">
            © 2024 FuntzGuntzAI
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#160A0D]">
        <div className="w-full max-w-md">
          {/* Mobile back link */}
          <Link 
            to="/" 
            className="lg:hidden flex items-center gap-2 text-[#C2B49A] hover:text-[#D4AF37] mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="mb-10">
            <h2 className="text-3xl font-cinzel font-bold text-[#F4EFE6] mb-3">
              Create account
            </h2>
            <p className="text-[#C2B49A] font-serif">
              Already have an account?{' '}
              <Link 
                to="/login" 
                state={location.state}
                className="text-[#D4AF37] hover:text-[#C5A059] font-medium"
              >
                Sign in
              </Link>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-cinzel font-medium text-[#F4EFE6]">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-terminal w-full rounded-lg"
                placeholder="John Doe"
                required
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-cinzel font-medium text-[#F4EFE6]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-terminal w-full rounded-lg"
                placeholder="you@example.com"
                required
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-cinzel font-medium text-[#F4EFE6]">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input-terminal w-full rounded-lg"
                placeholder="••••••••"
                required
                data-testid="password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-cinzel font-medium text-[#F4EFE6]">
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="input-terminal w-full rounded-lg"
                placeholder="••••••••"
                required
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-6 text-base rounded-lg"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-[#C2B49A]/60 font-serif">
            By creating an account, you agree to our Terms of Service
          </p>
        </div>
      </div>
    </div>
  );
}
